"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { useLanguage } from "../context/LanguageContext";
import { getSectionRoute } from "../lib/navigationRoutes";
import type { TalksSeriesMap } from "../lib/talksSeries";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "./ThemeToggle";

// NavSectionId is now defined centrally in lib/navigationTypes.ts
export type NavSectionId = import("../lib/navigationTypes").NavSectionId;

// NavOrderingKey is now just NavSectionId
// type NavOrderingKey = NavSectionId;

type HeaderNavItem = {
  label: string;
  testId: string;
  key: NavSectionId;
  id?: NavSectionId;
  href?: string;
};

const NAV_SECTION_IDS: NavSectionId[] = [
  "home",
  "roles",
  "approach",
  "decisions",
  "expertise",
  "talks-workshops",
  "thinking",
  "materials",
  "about",
  "contact"
];

function resolveActiveRouteId(pathname: string | null | undefined): NavSectionId | null {
  if (!pathname) return null;
  const normalized = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
  const segments = normalized.split("/").filter(Boolean);

  if (segments.length === 0) {
    return "home";
  }

  const [maybeLocale, maybeSection] = segments;
  if (maybeLocale !== "en" && maybeLocale !== "he") {
    return null;
  }

  if (!maybeSection) {
    return "home";
  }

  if (maybeSection === "insights" || maybeSection === "materials") {
    return "thinking";
  }

  return NAV_SECTION_IDS.includes(maybeSection as NavSectionId)
    ? (maybeSection as NavSectionId)
    : null;
}

type ThinkingSubRoute = "white-papers" | "github" | "ideas-posts" | "resources";

function resolveThinkingSubRoute(pathname: string | null | undefined): ThinkingSubRoute | null {
  if (!pathname) return null;
  const normalized = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
  const segments = normalized.split("/").filter(Boolean);

  if (segments.length < 2) return null;
  const [maybeLocale, maybeSection, maybeSub] = segments;
  if (maybeLocale !== "en" && maybeLocale !== "he") return null;
  if (maybeSection === "thinking") {
    if (maybeSub === "github") return "github";
    if (maybeSub === "ideas-posts") return "ideas-posts";
    if (maybeSub === "linkedin") return "ideas-posts";
    if (maybeSub === "resources") return "resources";
    return "white-papers";
  }
  if (maybeSection === "insights") {
    return maybeSub === "posts" ? "ideas-posts" : "resources";
  }
  if (maybeSection === "materials") {
    return "resources";
  }

  return null;
}

type TalksSubRoute = "team-lead-series" | "developer-ai-series";

function resolveTalksSubRoute(pathname: string | null | undefined): TalksSubRoute | null {
  if (!pathname) return null;
  const normalized = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
  const segments = normalized.split("/").filter(Boolean);

  if (segments.length < 2) return null;
  const [maybeLocale, maybeSection] = segments;
  if (maybeLocale !== "en" && maybeLocale !== "he") return null;
  if (maybeSection === "talks-workshops") {
    const maybeSub = segments[2];
    if (maybeSub === "team-lead-series" || maybeSub === "developer-ai-series") {
      return maybeSub;
    }
  }

  return null;
}

export function Header(): JSX.Element {
  const { t, locale } = useLanguage();
  const rtl = t.common.dir === "rtl";
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpenDesktop, setSolutionsOpenDesktop] = useState(false);
  const [solutionsOpenMobile, setSolutionsOpenMobile] = useState(false);
  const [talksOpenDesktop, setTalksOpenDesktop] = useState(false);
  const [talksOpenMobile, setTalksOpenMobile] = useState(false);
  const [thinkingOpenDesktop, setThinkingOpenDesktop] = useState(false);
  const [thinkingOpenMobile, setThinkingOpenMobile] = useState(false);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);
  const mobileMenuId = "mobile-navigation";
  const activeRouteId = resolveActiveRouteId(pathname);
  const thinkingSubRoute = resolveThinkingSubRoute(pathname);
  const talksSubRoute = resolveTalksSubRoute(pathname);

  useEffect(() => {
    if (!mobileOpen) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        mobileToggleRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) {
      setSolutionsOpenMobile(false);
      setTalksOpenMobile(false);
      setThinkingOpenMobile(false);
    }
  }, [mobileOpen]);

  const navItemsBase: HeaderNavItem[] = [
    { label: t.common.nav.home, key: "home", id: "home", testId: "home" },
    { label: t.common.nav.roles, key: "roles", id: "roles", testId: "roles" },
    { label: t.common.nav.approach, key: "approach", id: "approach", testId: "approach" },
    { label: t.common.nav.decisions, key: "decisions", id: "decisions", testId: "decisions" },
    { label: t.common.nav.expertise, key: "expertise", id: "expertise", testId: "expertise" },
    { label: t.common.nav.talksWorkshops, key: "talks-workshops", id: "talks-workshops", testId: "talks-workshops" },
    { label: t.common.nav.thinking, key: "thinking", id: "thinking", testId: "thinking" },
    { label: t.common.nav.about, key: "about", id: "about", testId: "about" },
    { label: t.common.nav.contact, key: "contact", id: "contact", testId: "contact" }
  ];

  const baseItems = navItemsBase;
  const solutionKeys: NavSectionId[] = ["roles", "approach", "decisions", "expertise"];
  const solutionItems = solutionKeys
    .map((key) => baseItems.find((item) => item.key === key))
    .filter((item): item is HeaderNavItem => Boolean(item));
  const navOrder: NavSectionId[] = ["home", "talks-workshops", "thinking", "about", "contact"];
  const navItems = [
    ...navOrder
      .map((key) => baseItems.find((item) => item.key === key && !solutionKeys.includes(item.key)))
      .filter((item): item is HeaderNavItem => Boolean(item)),
    ...baseItems.filter((item) => ![...navOrder, ...solutionKeys].includes(item.key))
  ];

  const getNavHref = (item: HeaderNavItem): string => {
    if (item.href) {
      return item.href;
    }

    if (item.id) {
      return getSectionRoute(locale, item.id);
    }

    return getSectionRoute(locale, "home");
  };

  const renderDesktopNavItem = (item: HeaderNavItem): JSX.Element => {
    const isActive = Boolean(item.id) && activeRouteId === item.id;
    const navClass = `nav-pill ${isActive ? "nav-pill-active" : "nav-pill-inactive"}`;
    const href = getNavHref(item);

    return (
      <Link
        key={item.testId}
        href={href}
        data-testid={`nav-${item.testId}-desktop`}
        className={navClass}
      >
        {item.label}
      </Link>
    );
  };

  const renderMobileNavItem = (item: HeaderNavItem): JSX.Element => {
    const isActive = Boolean(item.id) && activeRouteId === item.id;
    const navClass = `nav-pill w-full text-left ${isActive ? "nav-pill-active" : "nav-pill-inactive"}`;
    const href = getNavHref(item);

    return (
      <Link
        key={item.testId}
        href={href}
        onClick={() => setMobileOpen(false)}
        data-testid={`nav-${item.testId}-mobile`}
        className={navClass}
      >
        {item.label}
      </Link>
    );
  };

  const renderDesktopSolutionItem = (item: HeaderNavItem): JSX.Element => {
    const isActive = Boolean(item.id) && activeRouteId === item.id;
    const href = getNavHref(item);
    const itemClass = `flex items-center rounded-xl px-4 py-2 text-sm font-medium transition-colors ${isActive
      ? "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-100"
      : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
      }`;

    return (
      <Link
        key={`solutions-${item.testId}`}
        href={href}
        className={itemClass}
        onClick={() => setSolutionsOpenDesktop(false)}
        data-testid={`nav-${item.testId}-solutions-desktop`}
      >
        {item.label}
      </Link>
    );
  };

  const renderMobileSolutionItem = (item: HeaderNavItem): JSX.Element => {
    const isActive = Boolean(item.id) && activeRouteId === item.id;
    const href = getNavHref(item);
    const itemClass = `flex w-full items-center rounded-xl py-2 text-sm font-medium transition-colors ${rtl ? "pr-8" : "pl-8"} ${isActive
      ? "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-100"
      : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
      }`;

    return (
      <Link
        key={`solutions-mobile-${item.testId}`}
        href={href}
        className={itemClass}
        onClick={() => {
          setSolutionsOpenMobile(false);
          setMobileOpen(false);
        }}
        data-testid={`nav-${item.testId}-solutions-mobile`}
      >
        {item.label}
      </Link>
    );
  };

  const homeNavItem = navItems.find((item) => item.key === "home");
  const talksNavItem = navItems.find((item) => item.key === "talks-workshops");
  const thinkingNavItem = navItems.find((item) => item.key === "thinking");
  const remainingNavItems = navItems.filter(
    (item) => item.key !== "home" && item.key !== "thinking" && item.key !== "talks-workshops"
  );
  const solutionsActive = solutionItems.some((item) => item.id && activeRouteId === item.id);
  const solutionsLabel = t.common.nav.solutions;
  const talksLabel = t.common.nav.talksWorkshops;
  const talksSeriesOrder = (t.talksWorkshopsPage.order as string[]) ?? [];
  const talksSeriesMap = t.talksWorkshopsPage.series as TalksSeriesMap;
  type TalksItem = {
    label: string;
    href: string;
    testId: string;
    path: string;
    isActive: boolean;
  };
  const talksItems = talksSeriesOrder
    .map((key) => {
      const seriesEntry = talksSeriesMap[key];
      if (!seriesEntry) return null;
      const path = seriesEntry.path ?? key;
      return {
        label: seriesEntry.navLabel ?? seriesEntry.title ?? key,
        href: `/${locale}/talks-workshops/${path}`,
        testId: `talks-${path}`,
        path,
        isActive: talksSubRoute === path
      };
    })
    .filter((item): item is TalksItem => Boolean(item));
  const talksActive = Boolean(talksSubRoute);
  const thinkingActive = activeRouteId === "thinking";
  const thinkingLabel = t.common.nav.thinking;
  const thinkingItems = [
    {
      label: t.common.nav.posts,
      href: `/${locale}/thinking/ideas-posts`,
      testId: "thinking-ideas-posts",
      isActive: thinkingSubRoute === "ideas-posts"
    },
    {
      label: t.common.nav.whitePapers,
      href: `/${locale}/thinking/white-papers`,
      testId: "thinking-white-papers",
      isActive: thinkingSubRoute === "white-papers"
    },
    {
      label: t.common.nav.github,
      href: `/${locale}/thinking/github`,
      testId: "thinking-github",
      isActive: thinkingSubRoute === "github"
    },
    {
      label: t.common.nav.articles,
      href: `/${locale}/thinking/resources`,
      testId: "thinking-resources",
      isActive: thinkingSubRoute === "resources"
    }
  ] as const;

  const renderDesktopThinkingItem = (item: (typeof thinkingItems)[number]): JSX.Element => {
    const itemClass = `flex items-center rounded-xl px-4 py-2 text-sm font-medium transition-colors ${item.isActive
      ? "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-100"
      : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
      }`;

    return (
      <Link
        key={`thinking-${item.testId}`}
        href={item.href}
        className={itemClass}
        onClick={() => setThinkingOpenDesktop(false)}
        data-testid={`nav-${item.testId}-desktop`}
      >
        {item.label}
      </Link>
    );
  };

  const renderMobileThinkingItem = (item: (typeof thinkingItems)[number]): JSX.Element => {
    const itemClass = `flex w-full items-center rounded-xl py-2 text-sm font-medium transition-colors ${rtl ? "pr-8" : "pl-8"} ${item.isActive
      ? "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-100"
      : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
      }`;

    return (
      <Link
        key={`thinking-mobile-${item.testId}`}
        href={item.href}
        className={itemClass}
        onClick={() => {
          setThinkingOpenMobile(false);
          setMobileOpen(false);
        }}
        data-testid={`nav-${item.testId}-mobile`}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <header
      className="header-surface sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur"
      data-testid="site-header"
    >
      <div className="container-page py-4">
        <div className="flex items-center justify-between gap-3 md:gap-6">
          <Link
            href={getSectionRoute(locale, "home")}
            className="group flex items-center gap-3 text-start"
            aria-label="Return to top"
          >
            <Image
              src="/logo-mark.svg"
              alt="Meruki Moon logo"
              width={320}
              height={120}
              className="logo-motion h-16 w-auto sm:h-18 md:h-20"
              priority
            />
          </Link>
          <div className="hidden flex-1 justify-center md:flex">
            <nav className="flex items-center gap-4 lg:gap-6 text-sm font-medium">
              {homeNavItem && renderDesktopNavItem(homeNavItem)}
              {solutionItems.length > 0 && (
                <div
                  className="relative"
                  onMouseEnter={() => setSolutionsOpenDesktop(true)}
                  onMouseLeave={() => setSolutionsOpenDesktop(false)}
                  onFocus={() => setSolutionsOpenDesktop(true)}
                  onBlur={(event) => {
                    const next = event.relatedTarget as Node | null;
                    if (!next || !event.currentTarget.contains(next)) {
                      setSolutionsOpenDesktop(false);
                    }
                  }}
                >
                  <button
                    type="button"
                    className={`nav-pill flex items-center gap-1 whitespace-nowrap ${solutionsOpenDesktop || solutionsActive ? "nav-pill-active" : "nav-pill-inactive"
                      }`}
                    aria-haspopup="true"
                    aria-expanded={solutionsOpenDesktop}
                    onClick={() => setSolutionsOpenDesktop((prev) => !prev)}
                    data-testid="nav-solutions-button"
                  >
                    {solutionsLabel}
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 20 20"
                      className={`h-3 w-3 transition-transform ${solutionsOpenDesktop ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 7.5 10 12.5 15 7.5" />
                    </svg>
                  </button>
                  {solutionsOpenDesktop && (
                    <div className={`absolute ${rtl ? "right-0" : "left-0"} top-full pt-2`}>
                      <div className="w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                        <div className="flex flex-col gap-1">
                          {solutionItems.map((item) => renderDesktopSolutionItem(item))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {talksNavItem && (
                <div
                  className="relative"
                  onMouseEnter={() => setTalksOpenDesktop(true)}
                  onMouseLeave={() => setTalksOpenDesktop(false)}
                  onFocus={() => setTalksOpenDesktop(true)}
                  onBlur={(event) => {
                    const next = event.relatedTarget as Node | null;
                    if (!next || !event.currentTarget.contains(next)) {
                      setTalksOpenDesktop(false);
                    }
                  }}
                >
                  <button
                    type="button"
                    className={`nav-pill flex items-center gap-1 whitespace-nowrap ${talksOpenDesktop || talksActive ? "nav-pill-active" : "nav-pill-inactive"
                      }`}
                    aria-haspopup="true"
                    aria-expanded={talksOpenDesktop}
                    onClick={() => setTalksOpenDesktop((prev) => !prev)}
                    data-testid="nav-talks-workshops-button"
                  >
                    {talksLabel}
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 20 20"
                      className={`h-3 w-3 transition-transform ${talksOpenDesktop ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 7.5 10 12.5 15 7.5" />
                    </svg>
                  </button>
                  {talksOpenDesktop && (
                    <div className={`absolute ${rtl ? "right-0" : "left-0"} top-full pt-2`}>
                      <div className="w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                        <div className="flex flex-col gap-1">
                          {talksItems.map((item) => {
                            const itemClass = `flex items-center rounded-xl px-4 py-2 text-sm font-medium transition-colors ${item.isActive
                              ? "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-100"
                              : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                              }`;
                            return (
                              <Link
                                key={`talks-${item.testId}`}
                                href={item.href}
                                className={itemClass}
                                onClick={() => setTalksOpenDesktop(false)}
                                data-testid={`nav-${item.testId}-desktop`}
                              >
                                {item.label}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {thinkingNavItem && (
                <div
                  className="relative"
                  onMouseEnter={() => setThinkingOpenDesktop(true)}
                  onMouseLeave={() => setThinkingOpenDesktop(false)}
                  onFocus={() => setThinkingOpenDesktop(true)}
                  onBlur={(event) => {
                    const next = event.relatedTarget as Node | null;
                    if (!next || !event.currentTarget.contains(next)) {
                      setThinkingOpenDesktop(false);
                    }
                  }}
                >
                  <button
                    type="button"
                    className={`nav-pill flex items-center gap-1 whitespace-nowrap ${thinkingOpenDesktop || thinkingActive ? "nav-pill-active" : "nav-pill-inactive"
                      }`}
                    aria-haspopup="true"
                    aria-expanded={thinkingOpenDesktop}
                    onClick={() => setThinkingOpenDesktop((prev) => !prev)}
                    data-testid="nav-thinking-button"
                  >
                    {thinkingLabel}
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 20 20"
                      className={`h-3 w-3 transition-transform ${thinkingOpenDesktop ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 7.5 10 12.5 15 7.5" />
                    </svg>
                  </button>
                  {thinkingOpenDesktop && (
                    <div className={`absolute ${rtl ? "right-0" : "left-0"} top-full pt-2`}>
                      <div className="w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                        <div className="flex flex-col gap-1">
                          {thinkingItems.map((item) => renderDesktopThinkingItem(item))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {remainingNavItems.map((item) => renderDesktopNavItem(item))}
            </nav>
          </div>
          <div className="hidden items-center gap-3 md:flex">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
          <div className="flex items-center gap-2 md:hidden">
            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              ref={mobileToggleRef}
              className="rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:border-primary-200 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
              aria-expanded={mobileOpen}
              aria-controls={mobileMenuId}
              aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              {mobileOpen ? "Close" : "Menu"}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div
            id={mobileMenuId}
            role="menu"
            aria-label="Navigation menu"
            className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-lg dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex items-center justify-between gap-3">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
            <nav className="mt-4 flex flex-col gap-2" dir={rtl ? "rtl" : "ltr"}>
              {homeNavItem && renderMobileNavItem(homeNavItem)}
              {solutionItems.length > 0 && (
                <div className="w-full">
                  <button
                    type="button"
                    className={`nav-pill flex w-full items-center justify-between text-left ${solutionsOpenMobile || solutionsActive ? "nav-pill-active" : "nav-pill-inactive"
                      }`}
                    onClick={() => setSolutionsOpenMobile((prev) => !prev)}
                    aria-expanded={solutionsOpenMobile}
                    aria-controls="mobile-solutions-menu"
                  >
                    {solutionsLabel}
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 20 20"
                      className={`h-3 w-3 transition-transform ${solutionsOpenMobile ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 7.5 10 12.5 15 7.5" />
                    </svg>
                  </button>
                  {solutionsOpenMobile && (
                    <div id="mobile-solutions-menu" className="mt-2 space-y-2">
                      {solutionItems.map((item) =>
                        renderMobileSolutionItem(item)
                      )}
                    </div>
                  )}
                </div>
              )}
              {talksNavItem && (
                <div className="w-full">
                  <button
                    type="button"
                    className={`nav-pill flex w-full items-center justify-between text-left ${talksOpenMobile || talksActive ? "nav-pill-active" : "nav-pill-inactive"
                      }`}
                    onClick={() => setTalksOpenMobile((prev) => !prev)}
                    aria-expanded={talksOpenMobile}
                    aria-controls="mobile-talks-menu"
                  >
                    {talksLabel}
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 20 20"
                      className={`h-3 w-3 transition-transform ${talksOpenMobile ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 7.5 10 12.5 15 7.5" />
                    </svg>
                  </button>
                  {talksOpenMobile && (
                    <div id="mobile-talks-menu" className="mt-2 space-y-2">
                      {talksItems.map((item) => {
                        const itemClass = `flex w-full items-center rounded-xl py-2 text-sm font-medium transition-colors ${rtl ? "pr-8" : "pl-8"} ${item.isActive
                          ? "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-100"
                          : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                          }`;
                        return (
                          <Link
                            key={`talks-mobile-${item.testId}`}
                            href={item.href}
                            className={itemClass}
                            onClick={() => {
                              setTalksOpenMobile(false);
                              setMobileOpen(false);
                            }}
                            data-testid={`nav-${item.testId}-mobile`}
                          >
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {thinkingNavItem && (
                <div className="w-full">
                  <button
                    type="button"
                    className={`nav-pill flex w-full items-center justify-between text-left ${thinkingOpenMobile || thinkingActive ? "nav-pill-active" : "nav-pill-inactive"
                      }`}
                    onClick={() => setThinkingOpenMobile((prev) => !prev)}
                    aria-expanded={thinkingOpenMobile}
                    aria-controls="mobile-thinking-menu"
                  >
                    {thinkingLabel}
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 20 20"
                      className={`h-3 w-3 transition-transform ${thinkingOpenMobile ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 7.5 10 12.5 15 7.5" />
                    </svg>
                  </button>
                  {thinkingOpenMobile && (
                    <div id="mobile-thinking-menu" className="mt-2 space-y-2">
                      {thinkingItems.map((item) => renderMobileThinkingItem(item))}
                    </div>
                  )}
                </div>
              )}
              {remainingNavItems.map((item) => renderMobileNavItem(item))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
